import { useState } from "react";
import Login from "../templates/login/Login";
import { useDispatch } from "react-redux";
import AuthAPI from "../apis/auth/AuthAPI";
import { setCredentials } from "../redux/reducers/authReducer";
import { useNavigate } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const LoginScreen = () => {
  const authApi = new AuthAPI();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [appIsAvailable, setAppIsAvailable] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  // ── Mentions légales state ──
  const [showMentions, setShowMentions] = useState(false);
  const [isAcceptingMentions, setIsAcceptingMentions] = useState(false);
  const [mentionsError, setMentionsError] = useState("");
  const [loginError, setLoginError] = useState("");

  // ── Step 1: Login ──
  const login = async () => {
    setLoginError("");
    try {
      if (!executeRecaptcha) {
        setLoginError("reCAPTCHA non prêt, veuillez réessayer.");
        return;
      }

      const captchaToken = await executeRecaptcha("login");
      const response = await authApi.postLogin(email, password, captchaToken);

      // Check for error response (401, etc.)
      if (response.status !== 200) {
        // Extract error message from response
        const errorMessage = response.data?.detail || "Identifiants incorrects. Veuillez réessayer.";
        // Translate error message to French if needed
        setLoginError(translateErrorMessage(errorMessage));
        return;
      }

      const { first } = response.data.data;

      if (first === true || first === "true") {
        // ── First login: backend does NOT return a token yet.
        setShowMentions(true);
        return;
      }

      // ── Normal login: backend returns the token immediately.
      const accessToken = response.headers["x-access-token"];
      if (!accessToken) {
        setLoginError("Erreur d'authentification. Veuillez contacter l'administrateur.");
        console.error("No access token found in response headers (normal login)");
        return;
      }

      dispatch(setCredentials({ accessToken, user: response.data.data }));
      navigate("/action");

    } catch (error) {
      // Handle network errors or other exceptions
      let errorMsg = "Une erreur est survenue lors de la connexion. Veuillez réessayer.";
      
      // Check if error has response data
      if (error.response) {
        const detail = error.response.data?.detail;
        if (detail) {
          errorMsg = translateErrorMessage(detail);
        }
      }
      
      setLoginError(errorMsg);
      console.error("error login", error);
    }
  };

  // Helper function to translate error messages to French
  const translateErrorMessage = (message) => {
    // Convert to lowercase for case-insensitive comparison
    const lowerMsg = message.toLowerCase();
    
    if (lowerMsg.includes("invalid credentials") || 
        lowerMsg.includes("invalid email") || 
        lowerMsg.includes("invalid password") ||
        lowerMsg.includes("incorrect")) {
      return "Identifiants incorrects. Veuillez réessayer.";
    }
    
    if (lowerMsg.includes("email") && lowerMsg.includes("not found")) {
      return "Aucun compte trouvé avec cet email.";
    }
    
    if (lowerMsg.includes("account") && lowerMsg.includes("locked")) {
      return "Votre compte est verrouillé. Veuillez contacter l'administrateur.";
    }
    
    if (lowerMsg.includes("too many") && lowerMsg.includes("attempts")) {
      return "Trop de tentatives de connexion. Veuillez réessayer plus tard.";
    }
    
    if (lowerMsg.includes("expired")) {
      return "Votre session a expiré. Veuillez vous reconnecter.";
    }
    
    // Return the original message if no translation found
    return message;
  };

  // ── Step 2: Accept mentions légales ──
  const handleAcceptMentions = async () => {
    setIsAcceptingMentions(true);
    setMentionsError("");

    try {
      const response = await authApi.postAcceptMentions(email, password);

      if (response.status !== 200) {
        setMentionsError("Une erreur est survenue. Veuillez réessayer.");
        return;
      }

      setShowMentions(false);

    } catch (error) {
      setMentionsError("Une erreur est survenue. Veuillez réessayer.");
      console.error("error accepting mentions:", error);
    } finally {
      setIsAcceptingMentions(false);
    }
  };

  return (
    <Login
      navigate={navigate}
      login={login}
      email={email}
      password={password}
      onChangeEmail={setEmail}
      onChangePassword={setPassword}
      appIsAvailable={appIsAvailable}
      loginError={loginError}
      showMentions={showMentions}
      isAcceptingMentions={isAcceptingMentions}
      mentionsError={mentionsError}
      onAcceptMentions={handleAcceptMentions}
      onCloseMentions={() => setShowMentions(false)}
    />
  );
};

export default LoginScreen;